import Vue from 'vue';
import Component from 'vue-class-component';
import {Watch} from 'vue-property-decorator';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {Table, Icon, Popover, formatDate, FieldType, downloadCsv, arrayToCsv} from 'wc-lib';
import { NemRegions } from '../config';
import * as screenfull from 'screenfull';
import { IntervalRecord, mapApiDataToIntervals, setForecastIntervals } from './cumulative-price-common';

require('wc-lib/icons/downloadcircle.svg');
require('wc-lib/icons/fullscreen.svg');

@Component({
  template: require('./price-and-demand.html'),
  name: 'price-and-demand',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'wc-table': Table,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class NemPriceAndDemand extends Vue {
  gray: string = '#777DA7';
  orange: string = '#FE5F55';
  purple: string = '#B056BC';

  isFullscreen = false;
  data: Array<any> = [];
  cumulativePriceData: IntervalRecord[] = [];
  dynamicView: CustomDynamicView<any>;
  administeredPriceCap: number = null;
  marketPriceCap: number = null;
  cumulativePriceThreshold: number = null;
  regions = NemRegions;
  disabledRegions = [];
  selectedRegion: string = 'NSW1';
  selectedPeriod: string = '30MIN';
  selectedView: string = 'chart';
  currentInterval: any = null;
  nextInterval: any = null;
  intervalRefresh: any;
  id: string;
  guideColour = '#777da7';
  chartConfig: any  = null;
  tableColumns = [
    {
      title: 'Settlement Date',
      field: 'SETTLEMENTDATE',
      formatter: (value) => {
        let date = value.SETTLEMENTDATE || value;
        return formatDate(date, 'dd/MM/yyyy HH:mm');
      },
      sortable: true
    },
    {
      title: 'Spot Price ($/MWh)',
      field: 'RRP',
      fieldType: FieldType.Dollar,
      sortable: true
    },  
    {
      title: 'Scheduled Demand (MW)',
      field: 'TOTALDEMAND',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'Scheduled Generation (MW)',
      field: 'SCHEDULEDGENERATION',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'Semi Scheduled Generation (MW)',
      field: 'SEMISCHEDULEDGENERATION',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'Net Import (MW)',
      field: 'NETINTERCHANGE',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'Type',
      field: 'PERIODTYPE',
      sortable: true,
      formatter: (value, row, column) => { 
        if (value === 'ACTUAL') {
          return 'Actual';
        } else if (value === 'FORECAST') {
          return 'Forecast';
        }
        
        return value.PERIODTYPE;
      }
    }
  ];

  get isTrading() {
    return this.selectedPeriod === '30MIN';
  }
 
  get isRegionDisabled() {
    return this.disabledRegions.includes(this.selectedRegion);
  }

  created() {
    this.updateDisabledRegions();
    if (screenfull.isEnabled) {
      (screenfull as any).onchange(() => {
        this.isFullscreen = (screenfull as any).isFullscreen;
      });
    }
    this.fetchData();
    this.intervalRefresh = setInterval(() => {
      this.fetchData();
    }, 10000);
  }

  updateDisabledRegions() {
    if (this.$route.query.Elec_location) {
      const regions = (this.$route.query.Elec_location as string).split(',');
      this.disabledRegions = regions;
    }
  }

  selectRegion(region: string) {
    this.selectedRegion = region;
  }

  async fetchData() {
    if (!this.administeredPriceCap) {
      let id = await DataManager.addEndPoint('../api/report/NEM_DASHBOARD_MARKET_PRICE_LIMITS', undefined, 60000, 'NEM_DASHBOARD_MARKET_PRICE_LIMITS', undefined, 'yyyy-MM-ddTHH:mm:ss', []);   
      let data  = DataManager.getDynamicView(id, 'marketPriceLimits').data();
      this.administeredPriceCap = data.find(x => x.KEY === "AdministeredPriceCap").VALUE;
      this.marketPriceCap = data.find(x => x.KEY === "MarketPriceCap").VALUE;
      this.cumulativePriceThreshold = data.find(x => x.KEY === "CumulativePriceThreshold").VALUE;
    }
    
    let id = await DataManager.addEndPoint('../api/report/NEM_DASHBOARD_CUMUL_PRICE', undefined, 60000, 'NEM_DASHBOARD_CUMUL_PRICE', undefined, 'yyyy-MM-ddTHH:mm:ss', ['DT']);   
    let data = DataManager.getDynamicView(id, 'cumulativePrice').data();
    this.cumulativePriceData = setForecastIntervals(mapApiDataToIntervals(data)).filter(x => x.regionId === this.selectedRegion);

    this.id = await DataManager.addEndPoint('../api/report/5MIN', {"timeScale":[this.selectedPeriod]}, 60000, '5MIN', undefined, 'yyyy-MM-ddTHH:mm:ss', ['SETTLEMENTDATE']);   
    this.dynamicView = DataManager.getDynamicView(this.id, 'elecsummary');
    this.dynamicView.removeFilters();
    this.dynamicView.applyFind({ 'REGIONID': this.selectedRegion });
    this.applyFilter(this.dynamicView.data());
    this.chartConfig = this.getChartConfig();
  }

  applyFilter(data: any[]) {
    // current and next interval

    let currentInterval = null;
    let nextInterval = null;

    for (let i = data.length - 1; i >= 0; i--) {
      let row = data[i];
      if (row.PERIODTYPE === 'ACTUAL') {
        if (!currentInterval) {
          currentInterval = row;
          if ((i + 1) < data.length) {
            nextInterval = data[i+1];
          }
        }
      }
    }

    this.currentInterval = currentInterval;
    this.nextInterval = nextInterval;

    // add chart properties to data

    let currentIntervalReached: boolean = false;
    let lastLineColor: string = '';
    let keepApcOrangeEndTime: number = 0;
    for (let i=0; i<data.length; i++)
    {
      let row = data[i];

      currentIntervalReached = currentIntervalReached || row === currentInterval;

      // APC horizontal line
      row.APC = this.administeredPriceCap;
      row.apcLineColor = this.guideColour;
      if (this.isAdministeredPriceCapActiveOnInterval(row)) {
        row.apcLineColor = this.orange;

        let settlementDateTime: Date = row.SETTLEMENTDATE;
        let settlementDate: Date = new Date(settlementDateTime.getFullYear(), settlementDateTime.getMonth(), settlementDateTime.getDate());
        let millisecondsInHour: number = 3600 * 1000;
        if (settlementDateTime.getHours() < 4) {
          keepApcOrangeEndTime = settlementDate.getTime() + 4 * millisecondsInHour;
        }
        else {
          keepApcOrangeEndTime = settlementDate.getTime() + 28 * millisecondsInHour;
        }
      }
      else if (row.SETTLEMENTDATE.getTime() <= keepApcOrangeEndTime) {
        row.apcLineColor = this.orange;
      }

      // MPC code

      if (row.RRP >= this.marketPriceCap) {
        row.bullet = 'round';
      }

      let newLineColor: string = row.RRP >= this.marketPriceCap && i+1<data.length && data[i+1].RRP >= this.marketPriceCap ? this.orange : this.purple;
      if (newLineColor !== lastLineColor || row === currentInterval) {
        if (currentIntervalReached) {
          row.dashLength = 6;
        }
        row.lineColor = newLineColor;
        lastLineColor = newLineColor;
      }
    }

    this.data = data;
  }

  chartRendered(chart: any) {
    this.addChartLabels(chart);
    chart.addListener('drawn', (e: any) => {
      e.chart.clearLabels();
      this.addChartLabels(e.chart);
    });
  }

  addChartLabels(chart: any) {
    const yTitle = 26,
          yLabel = '!60',
          yValue = '!46',
          titleSize = 10,
          labelSize = 8,
          valueSize = 12,
          titleOpacity = 0.6,
          labelOpacity = 1,
          valueOpacity = 1;


    if (this.selectedPeriod !== '5MIN' && chart.categoryAxis.dateToCoordinate && this.currentInterval.SETTLEMENTDATE) {
      let xCoordinate = chart.categoryAxis.dateToCoordinate(this.currentInterval.SETTLEMENTDATE);
      if (xCoordinate) xCoordinate += 100;
      if (isNaN(xCoordinate)) return;

      const value = formatDate(this.currentInterval.SETTLEMENTDATE, "HH:mm dd MMM");
      chart.addLabel(xCoordinate, yTitle, 'FORECAST', 'left', titleSize, this.guideColour, 0, titleOpacity, true);
      chart.addLabel(xCoordinate, yValue, value, 'left', valueSize, this.guideColour, 0, valueOpacity, true);

      if (this.currentInterval.SETTLEMENTDATE) {
        chart.addLabel(xCoordinate, yLabel, 'CURRENT (SPOT) TIME', 'left', labelSize, this.guideColour, 0, labelOpacity, true);
      }
    }
  }

  @Watch('selectedPeriod')
  onSelectedPeriodChange(newValue: string, oldValue: string) {
    this.fetchData();
  }

  @Watch('selectedRegion')
  onSelectedRegionChange(newValue: string, oldValue: string) {
    this.fetchData();
  }

  getChartConfig() {
    const guides = [];
    if (this.selectedPeriod === '30MIN') {
      guides.push({
        date: this.currentInterval.SETTLEMENTDATE,
        color: this.guideColour,
        lineColor: "#777da7",
        lineAlpha: 1,
        fillAlpha: 0.1,
        dashLength: 3,
        lineThickness: 2,
        inside: false,
        above: true
      });
    }

    return {
      type: 'serial',
      marginRight: 0,
      marginLeft: 0,
      marginTop: 10,
      marginBottom: 0,
      addClassNames: true,
      panEventsEnabled: false,
      numberFormatter: {
        precision: 2, 
        decimalSeparator: '.', 
        thousandsSeparator: ','
      },
      valueAxes: [{
        id: "v1",
        position: "right",
        labelsEnabled: true,
        gridAlpha: 0,
        axisAlpha: 0,
        color: '#8ea9a9',
        title: 'DEMAND (MW)',
        titleColor: '#2c0831'
      }, {
        id: "v2",
        position: "left",
        labelsEnabled: true,
        gridAlpha: 0,
        axisAlpha: 0,
        color: '#8ea9a9',
        title: 'PRICE ($/MWh)',
        titleColor: '#2c0831'
      }],
      graphs: [ 
        { 
          valueAxis: "v1", 
          balloonText: 'DEMAND [[value]] MW', 
          precision: 0,
          valueField: 'TOTALDEMAND', 
          lineColor: '#34B9B3', 
          dashLengthField: "dashLength",
          bulletSize: 25, 
          bulletOffset: 10, 
          lineThickness: 5, 
          fillAlphas: 0.3,
          fillColors: ['#34B9B3', '#ffffff'],
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000'
          }
        },
        { 
          valueAxis: "v2", 
          balloonText: 'PRICE $[[value]]/MWh', 
          lineColorField: 'lineColor',
          valueField: 'RRP',
          bulletField: 'bullet',
          bulletColor: this.orange,
          bulletSize: 6, 
          lineThickness: 3, 
          dashLengthField: "dashLength",
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000',
            showBullet:false
          }
        },
        { 
          valueAxis: "v2",
          balloonText: `APC $${this.administeredPriceCap}/MWh`,
          balloonColorField: 'apcLineColor',
          dashLength: 3,
          lineColorField: 'apcLineColor',
          valueField: 'APC',
          lineThickness: 2
        }
      ],
      
      categoryAxis: {
        parseDates: true,
        //labelRotation: 45,
        minPeriod: "mm",
        color: '#8ea9a9',
        gridAlpha: 0,
        labelsEnabled: true,
        axisAlpha: 0
      },
      categoryField: "SETTLEMENTDATE",
      guides: guides,
      chartCursor: {
        zoomable: false,
        bulletsEnabled: true,
        bulletSize: 12,
        cursorColor: '#000000',
        categoryBalloonFunction: function(value) {
          var valueIndex = this.xToIndex(this.dateToCoordinate(value)),
              value = this.data[valueIndex],
              actual = value.dataContext.PERIODTYPE === 'ACTUAL',
              label = '';
          
          if (valueIndex == 0 ) {
            label = formatDate(value.dataContext.SETTLEMENTDATE, "HH:mm dd MMM");
          } else {
            label = formatDate(this.data[valueIndex-1].dataContext.SETTLEMENTDATE, "HH:mm-") + 
            formatDate(value.dataContext.SETTLEMENTDATE, "HH:mm dd MMM");
          } 
          
          label += actual ? ' Actual' : ' Forecast';
          
          return label;
        }
      }
    };
  }

  toggleFullscreen() {
    (screenfull as any).toggle();
  }

  download() {
    let rows = this.data;
    let columns = this.tableColumns;
    let csv = arrayToCsv(rows, columns);
    downloadCsv(`NEMPRICEANDDEMAND_${this.currentInterval.REGIONID}_${formatDate(this.currentInterval.SETTLEMENTDATE, 'yyyyMMddHHmm')}.csv`, csv);
  }

  destroyed() {
    clearInterval(this.intervalRefresh)
  }

  get isAdministeredPriceCapActive(): boolean {
    return this.isAdministeredPriceCapActiveOnInterval(this.currentInterval);
  }

  isAdministeredPriceCapActiveOnInterval(interval: any): boolean {
    if (interval.PERIODTYPE === 'ACTUAL') {
      return ((interval.APCFLAG & 1) === 1) && interval.RRP > 0;
    }

    if (this.cumulativePriceData.find(x =>
      x.dateTime.getTime() >= interval.SETTLEMENTDATE.getTime() - 25 * 60000 
      && x.dateTime.getTime() <= interval.SETTLEMENTDATE.getTime() 
      && x.cumulativePrice >= this.cumulativePriceThreshold)
    ) {
      return true;
    }

    return false;
  }

  isPriceCapped(price: number): boolean {
    return price >= this.marketPriceCap;
  }

}



// WEBPACK FOOTER //
// src/views/electricity/nem/price-and-demand.ts